/**
 * Modal utility functions for consistent modal behavior across the application
 */

import React from 'react';
import SvgClose from '@/components/shared/SvgClose';
import { heapTrack } from '@/lib/heap/api';

// Scroll lock utilities - disabled to prevent CLS from dual scroll lock mechanisms
const scrollLockUtils = {
  lock: () => {
    // Scroll lock disabled - handled by ModalProvider to prevent jiggle
  },

  unlock: () => {
    // Scroll lock disabled - handled by ModalProvider to prevent jiggle
  }
};

// React components that can use hooks
const ModalWrapper = ({
  modalType,
  isOpen,
  onClose,
  children,
  containerClassName = '',
  autoSize = true
}) => {
  const contentRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const returnFocusTo = React.useRef(null);

  // Apply scroll lock when modal is open
  React.useEffect(() => {
    if (isOpen) {
      scrollLockUtils.lock();
      return () => scrollLockUtils.unlock();
    }
  }, [isOpen]);

  // Save the element that should receive focus when modal closes
  React.useEffect(() => {
    if (isOpen) {
      // Save immediately when modal opens
      returnFocusTo.current = document.activeElement;
    }
  }, [isOpen]);

  // Focus trap
  React.useEffect(() => {
    if (!isOpen) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab' || !wrapperRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const wrapper = wrapperRef.current;
      const focusableElements = Array.from(
        wrapper.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => {
        const style = window.getComputedStyle(el);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          el.offsetWidth > 0 &&
          el.offsetHeight > 0
        );
      });

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (e.shiftKey) {
        if (currentIndex <= 0) {
          lastElement.focus();
        } else {
          focusableElements[currentIndex - 1].focus();
        }
      } else {
        if (
          currentIndex === -1 ||
          currentIndex >= focusableElements.length - 1
        ) {
          firstElement.focus();
        } else {
          focusableElements[currentIndex + 1].focus();
        }
      }
    };

    // Focus first element when modal opens
    setTimeout(() => {
      if (wrapperRef.current) {
        const focusableElements = Array.from(
          wrapperRef.current.querySelectorAll(
            'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => {
          const style = window.getComputedStyle(el);
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            style.opacity !== '0' &&
            el.offsetWidth > 0 &&
            el.offsetHeight > 0
          );
        });

        focusableElements[0]?.focus({ preventScroll: true });
      }
    }, 0);

    document.addEventListener('keydown', handleTabKey, true);

    return () => {
      document.removeEventListener('keydown', handleTabKey, true);

      // Restore focus immediately in cleanup
      if (
        returnFocusTo.current &&
        document.body.contains(returnFocusTo.current)
      ) {
        returnFocusTo.current.focus();

        setTimeout(() => {
          if (
            returnFocusTo.current &&
            document.body.contains(returnFocusTo.current)
          ) {
            returnFocusTo.current.focus();
          }
        }, 0);
      }
    };
  }, [isOpen]);

  // Simple height class - let CSS handle the sizing
  const getHeightClass = () => {
    if (!autoSize) return '';
    return 'max-h-[calc(100svh-32px)] md:max-h-[calc(100svh-64px)]';
  };

  if (!isOpen) return null;

  return (
    <div
      ref={wrapperRef}
      className={`modal-wrapper fixed inset-0 z-9999 flex items-center justify-center bg-black/50 p-4 transition-opacity duration-300 ease-out md:p-8`}
      role="dialog"
      aria-modal="true"
      data-cy={`modal-wrapper-${modalType}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          heapTrack('Leesa Modal Closed', {
            'Leesa - Modal Type': modalType ?? 'N/A',
            'Leesa - Modal Close Method': 'Click Outside'
          });
          onClose();
        }
      }}
    >
      <div
        className={`animate-in fade-in zoom-in-95 border-dawn-grey-30 relative z-10 w-full max-w-200 px-5 pt-10 ${getHeightClass()} ${containerClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
};

export const modalUtils = {
  // Common close button configurations
  closeButtonConfigs: {
    // Basic close button (used in simple modals)
    basic: (onClose, strokeColor = '#000000') => ({
      className: 'absolute top-4 right-4 px-2',
      onClick: onClose,
      'aria-label': 'Close modal',
      children: <SvgClose stroke={strokeColor} type="x" />
    }),

    // Enhanced close button with hover effects for light backgrounds
    enhanced: (onClose, strokeColor = '#000000') => ({
      className:
        'absolute top-4 right-4 flex h-8 w-8 items-center justify-center transition-colors ',
      onClick: onClose,
      'aria-label': 'Close modal',
      children: <SvgClose stroke={strokeColor} type="x" />
    })
  },

  // Create standardized close button
  createCloseButton: (modalType, onClose, variant = 'basic', strokeColor) => {
    const config = modalUtils.closeButtonConfigs[variant];
    if (!config) {
      throw new Error(`Unknown close button variant: ${variant}`);
    }

    // Wrap onClose to add tracking to heap
    const onCloseHandler = () => {
      heapTrack('Leesa Modal Closed', {
        'Leesa - Modal Type': modalType ?? 'N/A',
        'Leesa - Modal Close Method': 'Close Button'
      });
      onClose();
    };

    const buttonProps = config(onCloseHandler, strokeColor);
    buttonProps['data-cy'] = 'modal-close-button';

    return React.createElement('button', buttonProps);
  },

  // Container class presets organized by theme and use case
  containerPresets: {
    // Standard modal presets
    standard: {
      basic:
        'overflow-auto bg-white text-left text-black shadow-2xl transition-all sm:max-w-200 sm:p-10 pb-8'
    },
    // Cross-sell and promotional modals
    crossSell: {
      green:
        'overflow-auto  bg-parlor-green-115 flex flex-col text-left text-white sm:max-w-200 sm:pb-8',
      greenInterstitial:
        'bg-parlor-green-115 relative overflow-x-hidden overflow-y-auto max-sm:max-w-screen sm:m-8 sm:max-w-200 sm:p-10 pb-8'
    }
  },

  // Legacy flat access for backward compatibility
  get basic() {
    return this.containerPresets.standard.basic;
  },
  get green() {
    return this.containerPresets.crossSell.green;
  },
  get greenInterstitial() {
    return this.containerPresets.crossSell.greenInterstitial;
  },

  // Complete modal wrapper - returns JSX structure with scroll lock
  createModalWrapper: ({
    modalType,
    isOpen,
    onClose,
    children,
    containerClassName = '',
    backdropClassName = '',
    autoSize = true,
    closeButton = false,
    closeButtonVariant = 'basic',
    closeButtonStroke
  }) => {
    const wrappedChildren = closeButton ? (
      <>
        {modalUtils.createCloseButton(
          modalType,
          onClose,
          closeButtonVariant,
          closeButtonStroke
        )}
        {children}
      </>
    ) : (
      children
    );

    return (
      <ModalWrapper
        modalType={modalType}
        isOpen={isOpen}
        onClose={onClose}
        containerClassName={containerClassName}
        backdropClassName={backdropClassName}
        autoSize={autoSize}
      >
        {wrappedChildren}
      </ModalWrapper>
    );
  }
};

export default modalUtils;
